import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, Dictionary } from '@reduxjs/toolkit';
import { Billing, PaymentPlan, ReduxEntity, RegistrationSetting } from 'Douleutaras/types';
import { BillingDataClass } from 'Douleutaras/redux/data/settings/utils/BillingDataClass';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { RootState } from 'Douleutaras/redux/store';
import each from 'lodash/each';
import sortBy from 'lodash/sortBy';

const BILLINGS_ENDPOINT = 'api/public/billings';

const billingsAdapter = createEntityAdapter<Billing>({
  selectId: item => item.id
});

export const getBillings = createAsyncThunk(
  'settings/billings/get',
  async (args: Dictionary<string | boolean> = {}, api) => {
    const response = await makeRequest(
      `${BILLINGS_ENDPOINT}/`,
      {
        method: 'GET',
        additionalGetParams: args
      }
    )(api);

    return sortBy(response.data, (o: {price: string}) => parseFloat(o.price));
  }
);

export const getBilling = createAsyncThunk(
  'settings/billing/get',
  async ({ id, args = {} }: { id: string, args?: Dictionary<string | boolean> }, api) => {
    const response = await makeRequest(
      `${BILLINGS_ENDPOINT}/${id}`,
      {
        method: 'GET',
        additionalGetParams: args
      }
    )(api);

    return response.data;
  }
);

export const billingsSlice = createSlice({
  name: 'billings',
  initialState: billingsAdapter.getInitialState({
    loading: false,
    error: false,
    entities: []
  } as ReduxEntity<PaymentPlan>),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getBillings.pending, state => {
      state.loading = true;
    });
    builder.addCase(getBillings.fulfilled, (state, action) => {
      state.loading = false;
      billingsAdapter.setMany(state, action.payload);
    });
    builder.addCase(getBilling.pending, state => {
      state.loading = true;
    });
    builder.addCase(getBilling.fulfilled, (state, action) => {
      state.loading = false;
      billingsAdapter.setOne(state, action.payload);
    });
  }
});

export const selectBillingData = (regSettings, billings) => createSelector(regSettings, billings, (
  registrationSettings: Array<RegistrationSetting>,
  paymentPlans: Array<PaymentPlan>
): BillingDataClass => {
  if (!paymentPlans.length) {
    return;
  }

  const data: BillingDataClass = new BillingDataClass();

  if (registrationSettings.length) {
    each<RegistrationSetting>(registrationSettings, registration => {
      if (registration && registration.products[0] && registration.products[0].credits > 0) {
        data.autoTopUp = registration;
      }
    });
  }

  each<PaymentPlan>(paymentPlans, plan => {
    // Apply product to the creditData
    if (plan.credits > 0 && plan.bids === 0) {
      data.addProduct(plan);
    }

    // Form the autotopup information
    if (data.hasAutoTopProduct(plan.id)) {
      data.currentProd = plan;
    }
  });

  return data;
});

export const billingsSelector = billingsAdapter.getSelectors<RootState>(state => state.billings);

export const selectSubscriptionPlans = (state: RootState) => state.billings.entities.filter(item => item.channel === 'subscription');
export const selectCreditsPlans = (state: RootState) => state.billings.entities.filter(item => item.channel === 'credits');
export const selectAutoTopPlans = (state: RootState) => state.billings.entities.filter(item => item.autotopup);
export const selectBackendPlans = (state: RootState, backend: string) => state.billings.entities.filter(item => item.backend === backend);
